var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { Fragment, useRef, useCallback, useReducer, useEffect, useState, } from 'react';
import { Waypoint } from 'react-waypoint';
import styled from '@emotion/styled';
import UspBar from '../../usp-bar/components/UspBar';
import Toolbar from '../../toolbar/components/Toolbar';
import TopNavigationBar from '../../top-navigation-bar/components/TopNavigationBar';
import cookieStorage from '../../common/CookieStorage';
import { experiments, isExperimentActive, } from '../../common/audienceSegmentation';
// This is a simplified version of the code from the react-waypoint library
// that helps to find the first scrollable ancestor of a DOM node
//
// https://github.com/brigade/react-waypoint/blob/6004756ad6b6699f358fc6008e29e242b2777379/src/waypoint.jsx#L134-L168
function findScrollableAncestor(node) {
    while (node && node.parentNode) {
        node = node.parentNode;
        if (node === document.body) {
            // We've reached all the way to the root node.
            return window;
        }
        var style = window.getComputedStyle(node);
        var overflow = style.getPropertyValue('overflow-y') ||
            style.getPropertyValue('overflow');
        if (overflow === 'auto' || overflow === 'scroll') {
            return node;
        }
    }
    // A scrollable ancestor element was not found, which means that we need to
    // do stuff on window.
    return window;
}
/**
 * Returns scroll value of either a window or a dom node. Falls back to
 * zero if no value found
 *
 * @param {HTMLElement | Window | null} scrollable Scrollable DOM node or null
 * @returns {number}
 */
function getScrollTop(scrollable) {
    if (scrollable) {
        // If scrollable element is a window, we check the offset
        // with `pageYOffset`, otherwise we are checking `scrollTop`
        return scrollable.pageYOffset || scrollable.scrollTop || 0;
    }
    else {
        return 0;
    }
}
function useOnScrollDirectionChange(callback, ref) {
    var prevScrollDirection = useRef(null);
    var prevScrollTop = useRef(0);
    useEffect(function () {
        var scrollable = findScrollableAncestor(ref.current);
        prevScrollTop.current = getScrollTop(scrollable);
        var listener = function () {
            requestAnimationFrame(function () {
                var scrollTop = getScrollTop(scrollable);
                var currentDirection = scrollTop < prevScrollTop.current
                    ? useOnScrollDirectionChange.up
                    : scrollTop > prevScrollTop.current
                        ? useOnScrollDirectionChange.down
                        : null;
                if (currentDirection !== prevScrollDirection.current) {
                    callback({
                        currentDirection: currentDirection,
                        previousDirection: prevScrollDirection.current,
                    });
                }
                prevScrollDirection.current = currentDirection;
                prevScrollTop.current = scrollTop;
            });
        };
        scrollable.addEventListener('scroll', listener, { passive: true });
        return function () { return scrollable.removeEventListener('scroll', listener); };
    }, [callback, ref]);
}
useOnScrollDirectionChange.up = 'up';
useOnScrollDirectionChange.down = 'down';
var StickyContainer = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", ";\n"], ["\n  ",
    ";\n"])), function (props) {
    return props.sticky &&
        "\n      position: fixed;\n      min-width: 100%;\n      top: " + (props.visible ? 0 : "-" + props.top + "px") + ";\n      box-shadow: " + (props.visible ? '0 0 8px rgba(0, 0, 0, 0.2)' : 'none') + ";\n      transition: box-shadow 0.2s ease-out, top 0.2s ease-out;\n      z-index: 1000;\n    ";
});
function getContentHeight(element) {
    return element ? element.offsetHeight : 0;
}
var containerInitialState = {
    sticky: false,
    visible: false,
    height: 0,
};
var containerReducer = function (state, action) {
    var height = action.payload.height;
    switch (action.type) {
        case 'positionChange':
            return __assign(__assign({}, state), { height: height, sticky: action.payload.currentPosition !== Waypoint.inside });
        case 'directionChange':
            return __assign(__assign({}, state), { height: height, visible: action.payload.currentDirection === useOnScrollDirectionChange.up });
        default:
            return state;
    }
};
function ShowOnScrollUpContainer(_a) {
    var children = _a.children;
    var container = useRef(null);
    var _b = __read(useReducer(containerReducer, containerInitialState), 2), state = _b[0], dispatch = _b[1];
    var onScrollDirectionChange = useCallback(function (_a) {
        var currentDirection = _a.currentDirection;
        dispatch({
            type: 'directionChange',
            payload: {
                currentDirection: currentDirection,
                height: getContentHeight(container.current),
            },
        });
    }, [dispatch]);
    var onPositionChange = useCallback(function (_a) {
        var currentPosition = _a.currentPosition;
        dispatch({
            type: 'positionChange',
            payload: {
                currentPosition: currentPosition,
                height: getContentHeight(container.current),
            },
        });
    }, [dispatch]);
    useOnScrollDirectionChange(onScrollDirectionChange, container);
    return (React.createElement(Fragment, null,
        React.createElement(Waypoint, { onPositionChange: onPositionChange },
            React.createElement("div", null,
                React.createElement(StickyContainer, { ref: container, sticky: state.sticky, visible: state.visible, top: state.height, "data-testid": "sticky-container" }, children))),
        React.createElement("div", { "aria-hidden": "true", style: {
                display: state.sticky === true ? 'block' : 'none',
                height: state.height,
            } })));
}
var GlobalSectionHeaderNavigation = function (_a) {
    var uspBar = _a.uspBar, navigation = _a.navigation, defaultSticky = _a.sticky, __legacy_shop_reboot_max_width = _a.__legacy_shop_reboot_max_width;
    var _b = __read(useState(defaultSticky), 2), sticky = _b[0], setSticky = _b[1];
    useEffect(function () {
        var shouldStick = isExperimentActive(cookieStorage.getExperimentVariant(), experiments.STICKY_HEADER);
        setSticky(shouldStick);
    }, []);
    var Wrapper = (sticky
        ? ShowOnScrollUpContainer
        : Fragment);
    return (React.createElement(Fragment, null,
        React.createElement(UspBar, __assign({}, uspBar, { __legacy_shop_reboot_max_width: __legacy_shop_reboot_max_width })),
        React.createElement(Wrapper, null,
            React.createElement(Toolbar, { __legacy_shop_reboot_max_width: __legacy_shop_reboot_max_width }),
            navigation && (React.createElement(TopNavigationBar, { navigation: navigation, __legacy_shop_reboot_max_width: __legacy_shop_reboot_max_width })))));
};
GlobalSectionHeaderNavigation.defaultProps = {
    sticky: false,
    __legacy_shop_reboot_max_width: true,
};
export default GlobalSectionHeaderNavigation;
var templateObject_1;
